import React, { useState, useEffect } from 'react';
import { postsAPI, promptsAPI, referencesAPI } from '../services/api';
import { Sparkles, Lightbulb, Save, Loader } from 'lucide-react';
import '../styles/generator.css';

const ArticleGenerator = ({ onArticleGenerated }) => {
    const [loading, setLoading] = useState(false);
    const [generatingTitles, setGeneratingTitles] = useState(false);
    
    const [formData, setFormData] = useState({
        topic: '',
        keywords: '',
        tone: 'professional',
        length: 'medium',
        saveLocal: false,
        sendWpDraft: false,
        sendWpPublish: false,
        categories: []
    });

    const [generatedArticle, setGeneratedArticle] = useState(null);
    const [suggestedTitles, setSuggestedTitles] = useState([]);

    // Prompts e Referências
    const [prompts, setPrompts] = useState([]);
    const [selectedPromptId, setSelectedPromptId] = useState(null);
    // Criação de Prompt (Template)
    const [showCreatePromptModal, setShowCreatePromptModal] = useState(false);
    const [newPromptTitle, setNewPromptTitle] = useState('');
    const [newPromptContent, setNewPromptContent] = useState('');
    const [creatingPrompt, setCreatingPrompt] = useState(false);

    const [references, setReferences] = useState([]);
    const [selectedReferences, setSelectedReferences] = useState([]);

    // Formulários de criação de referência
    const [newRefTitle, setNewRefTitle] = useState('');
    const [newRefUrl, setNewRefUrl] = useState('');

    const [newFileTitle, setNewFileTitle] = useState('');
    const [newFileFile, setNewFileFile] = useState(null);

    const defaultSystemInstruction = "Você é um escritor especializado em criar conteúdo de blog de alta qualidade em português brasileiro.";
    const [systemInstruction, setSystemInstruction] = useState(defaultSystemInstruction);

    useEffect(() => {
        loadPrompts();
        loadReferences();
    }, []);

    const loadPrompts = async () => {
        try {
            const response = await promptsAPI.list();
            setPrompts(response.data?.data || []);
        } catch (error) {
            console.error('Erro ao carregar prompts:', error);
        }
    };

    const loadReferences = async () => {
        try {
            const response = await referencesAPI.list();
            setReferences(response.data?.data || []);
        } catch (error) {
            console.error('Erro ao carregar referências:', error);
        }
    };

    // Handlers de criação de Prompt (Template)
    const openPromptModal = () => {
        setShowCreatePromptModal(true);
        setNewPromptTitle('');
        setNewPromptContent('');
    };

    const closePromptModal = () => {
        setShowCreatePromptModal(false);
    };

    const saveNewPrompt = async () => {
        if (!newPromptTitle.trim() || !newPromptContent.trim()) {
            alert('Defina um título e o conteúdo do prompt');
            return;
        }
        try {
            setCreatingPrompt(true);
            const resp = await promptsAPI.create({
                name: newPromptTitle.trim(),
                prompt_template: newPromptContent.trim()
            });
            const created = resp.data?.data;
            if (created?.id) {
                alert('Prompt criado com sucesso! Ele será usado na próxima geração de artigo.');
                setShowCreatePromptModal(false);
                await loadPrompts();
                setSelectedPromptId(created.id);
                console.log('Prompt criado e selecionado:', created.id);
            } else {
                alert('Falha ao criar prompt');
            }
        } catch (error) {
            alert('Erro ao criar prompt: ' + (error.response?.data?.error || error.message));
        } finally {
            setCreatingPrompt(false);
        }
    };
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === 'sendWpDraft') {
            setFormData(prev => ({ ...prev, sendWpDraft: checked, sendWpPublish: checked ? false : prev.sendWpPublish }));
            return;
        }
        if (name === 'sendWpPublish') {
            setFormData(prev => ({ ...prev, sendWpPublish: checked, sendWpDraft: checked ? false : prev.sendWpDraft }));
            return;
        }
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const toggleReference = (refId) => {
        setSelectedReferences(prev => (
            prev.includes(refId) ? prev.filter(id => id !== refId) : [...prev, refId]
        ));
    };

    const generateTitles = async () => {
        const rawTopic = formData.topic;
        console.log('[UI] generateTitles clicked. topic input=', rawTopic);

        if (!formData.topic.trim()) {
            alert('Por favor, insira um tópico');
            console.warn('[UI] Aborting: topic is empty');
            return;
        }

        setGeneratingTitles(true);
        console.log('[UI] setGeneratingTitles -> true');
        try {
            const trimmedTopic = formData.topic.trim();
            const url = (typeof window !== 'undefined' && window.__API_BASE__) ? `${window.__API_BASE__}/api/posts/generate-titles` : '/api/posts/generate-titles';
            console.log('[API] Calling generate-titles:', url, 'payload=', { topic: trimmedTopic });

            const response = await postsAPI.generateTitles(trimmedTopic);
            const payload = response?.data || {};
            const titles = payload?.data || payload?.titles || [];
            console.log('[API] Response generate-titles:', { status: response?.status, success: payload?.success, titlesCount: Array.isArray(titles) ? titles.length : 'n/a', payload });

            if (payload?.success) {
                const finalTitles = Array.isArray(titles) ? titles : [];
                console.log('[UI] setSuggestedTitles ->', finalTitles);
                setSuggestedTitles(finalTitles);
            } else {
                const msg = payload?.error || 'Erro desconhecido';
                console.error('[UI] generateTitles error (payload)', msg, payload);
                alert('Erro ao gerar tópicos: ' + msg);
            }
        } catch (error) {
            const status = error?.response?.status;
            const data = error?.response?.data;
            console.error('[API] generateTitles exception:', { message: error.message, status, data, error });
            alert('Erro ao gerar tópicos: ' + (data?.error || error.message));
        } finally {
            setGeneratingTitles(false);
            console.log('[UI] setGeneratingTitles -> false');
        }
    };

    const generateArticle = async () => {
        if (!formData.topic.trim()) {
            alert('Por favor, insira um tópico');
            return;
        }

        setLoading(true);
        try {
            const keywords = formData.keywords.split(',').map(k => k.trim()).filter(k => k);
            const requestData = {
                topic: formData.topic.trim(),
                keywords,
                tone: formData.tone,
                length: formData.length,
                categories: formData.categories,
                templateId: selectedPromptId,
                references: selectedReferences,
                systemInstruction: systemInstruction || defaultSystemInstruction
            };

            console.log('Enviando dados:', requestData);

            const response = await postsAPI.generateArticle(requestData);

            if (response.data.success) {
                const article = response.data.data;
                setGeneratedArticle(article);

                // Fluxo pós-geração baseado nos checkboxes
                if (formData.saveLocal || formData.sendWpDraft || formData.sendWpPublish) {
                    // Salvar localmente primeiro (necessário para obter postId)
                    const saveResult = await saveGeneratedArticle(article, (formData.sendWpDraft || formData.sendWpPublish));
                    const postId = saveResult?.id;

                    if (!postId) {
                        throw new Error('Falha ao obter ID do post após salvar');
                    }

                    // Envio ao WordPress conforme seleção
                    const wpPayload = {
                        categories: [],
                        tags: Array.isArray(article.tags) ? article.tags : [],
                        excerpt: article.excerpt || undefined
                    };

                    if (formData.sendWpDraft) {
                        const wpResp = await postsAPI.sendDraftToWordPress(postId, wpPayload);
                        if (wpResp.data?.success) {
                            alert('Enviado ao WordPress como rascunho com sucesso!');
                        } else {
                            alert('Erro ao enviar ao WordPress: ' + (wpResp.data?.error || 'Erro desconhecido'));
                        }
                    } else if (formData.sendWpPublish) {
                        const wpResp = await postsAPI.publishToWordPress(postId, wpPayload);
                        if (wpResp.data?.success) {
                            alert('Publicado no WordPress com sucesso!');
                        } else {
                            alert('Erro ao publicar no WordPress: ' + (wpResp.data?.error || 'Erro desconhecido'));
                        }
                    }

                    if (onArticleGenerated) {
                        onArticleGenerated();
                    }
                }
            } else {
                alert('Erro ao gerar artigo: ' + (response.data.error || 'Erro desconhecido'));
            }
        } catch (error) {
            console.error('Erro ao gerar artigo:', error);
            alert('Erro ao gerar artigo: ' + (error.response?.data?.error || error.message));
        } finally {
            setLoading(false);
        }
    };

    const saveGeneratedArticle = async (articleArg = generatedArticle, silent = false) => {
        console.log('🎯 === SALVAMENTO DE ARTIGO INICIADO ===');
        
        // Se o primeiro argumento for um evento de clique, use o estado generatedArticle
        const articleToSave = (articleArg && typeof articleArg === 'object' && ('nativeEvent' in articleArg || 'target' in articleArg))
            ? generatedArticle
            : articleArg;
        
        if (!articleToSave) {
            console.log('❌ Nenhum artigo para salvar');
            alert('Nenhum artigo gerado para salvar');
            return;
        }
    
        console.log('📄 Artigo gerado:', articleToSave);
        console.log('📂 Categorias selecionadas:', formData.categories);
    
        try {
            // ✅ PREPARAR DADOS COM VALIDAÇÃO RIGOROSA
            const postData = {
                title: articleToSave.title && String(articleToSave.title).trim() !== '' 
                    ? String(articleToSave.title).trim() 
                    : 'Título não definido',
                
                content: articleToSave.content && String(articleToSave.content).trim() !== '' 
                    ? String(articleToSave.content).trim() 
                    : '<p>Conteúdo não disponível</p>',
                
                excerpt: articleToSave.excerpt && String(articleToSave.excerpt).trim() !== '' 
                    ? String(articleToSave.excerpt).trim() 
                    : null,
                
                tags: Array.isArray(articleToSave.tags) 
                    ? articleToSave.tags.filter(tag => tag && String(tag).trim() !== '') 
                    : [],
                
                categories: Array.isArray(formData.categories) 
                    ? formData.categories.filter(cat => cat !== null && cat !== undefined && !isNaN(cat))
                    : [],
                
                status: 'draft'
            };
    
            console.log('📤 Dados preparados para envio:');
            console.log('- title:', postData.title);
            console.log('- content length:', postData.content.length);
            console.log('- excerpt:', postData.excerpt);
            console.log('- tags:', postData.tags);
            console.log('- categories:', postData.categories);
            console.log('- status:', postData.status);
    
            // ✅ ENVIAR PARA API
            console.log('🚀 Enviando para API...');
            const response = await postsAPI.createPost(postData);
            
            console.log('✅ Resposta da API:', response.data);
            
            if (response.data.success) {
                console.log('🎉 Post salvo com sucesso!');
                
                if (!silent) {
                    if (onArticleGenerated) {
                        onArticleGenerated();
                    }
                    // Usar o título do artigo original ou o título retornado pela API
                    const savedTitle = response.data.title || postData.title || 'Artigo';
                    alert(`Artigo "${savedTitle}" salvo com sucesso!`);
                    setGeneratedArticle(null);
                }
                return response.data;
            } else {
                throw new Error(response.data.error || 'Erro desconhecido ao salvar');
            }
            
        } catch (error) {
            console.error('❌ === ERRO AO SALVAR ARTIGO ===');
            console.error('Erro completo:', error);
            console.error('Response data:', error.response?.data);
            console.error('Response status:', error.response?.status);
            
            const errorMessage = error.response?.data?.error || error.message || 'Erro desconhecido';
            alert(`Erro ao salvar artigo: ${errorMessage}`);
        }
};

    const clearForm = () => {
        setFormData({
            topic: '',
            keywords: '',
            tone: 'professional',
            length: 'medium',
            saveLocal: false,
            sendWpDraft: false,
            sendWpPublish: false,
            categories: []
        });
        setGeneratedArticle(null);
        setSuggestedTitles([]);
        setSelectedPromptId(null);
        setSelectedReferences([]);
    };

    const addUrlReference = async () => {
        if (!newRefUrl.trim()) {
            alert('Insira uma URL válida');
            return;
        }
        try {
            await referencesAPI.createUrl({ title: newRefTitle || null, url: newRefUrl.trim() });
            setNewRefTitle('');
            setNewRefUrl('');
            await loadReferences();
            alert('Referência URL adicionada com sucesso!');
        } catch (error) {
            alert('Erro ao adicionar referência URL: ' + (error.response?.data?.error || error.message));
        }
    };

    const uploadFileReference = async () => {
        if (!newFileFile) {
            alert('Selecione um arquivo .txt');
            return;
        }
        try {
            const form = new FormData();
            form.append('file', newFileFile);
            if (newFileTitle) form.append('title', newFileTitle);
            await referencesAPI.uploadFile(form);
            setNewFileFile(null);
            setNewFileTitle('');
            await loadReferences();
            alert('Arquivo de referência enviado com sucesso!');
        } catch (error) {
            alert('Erro ao enviar arquivo: ' + (error.response?.data?.error || error.message));
        }
    };

    return (
        <div className="article-generator">
            <div className="generator-header">
                <h2><Sparkles size={24} /> Gerador de Artigos com IA</h2>
                <p>Crie conteúdo de qualidade automaticamente</p>
            </div>

            <div className="generator-form">
                <div className="form-section">
                    <div className="form-group">
                        <label>Tópico do Artigo:</label>
                        <div className="input-with-button">
                            <input
                                type="text"
                                name="topic"
                                value={formData.topic}
                                onChange={handleInputChange}
                                placeholder="Ex: Como criar um blog de sucesso em 2024"
                            />
                            <button 
                                onClick={generateTitles} 
                                disabled={generatingTitles || !formData.topic.trim()}
                                className="suggest-btn"
                            >
                                {generatingTitles ? (
                                    <Loader size={16} className="spinning" />
                                ) : (
                                    <Lightbulb size={16} />
                                )}
                                Sugerir Tópicos
                            </button>
                        </div>
                    </div>

                    {suggestedTitles.length > 0 && (
                        <div className="suggested-titles">
                            <h4>💡 Tópicos Sugeridos:</h4>
                            <div className="titles-grid">
                                {suggestedTitles.map((title, index) => (
                                    <div 
                                        key={index} 
                                        className="title-suggestion"
                                        onClick={() => setFormData(prev => ({ ...prev, topic: title }))}
                                    >
                                        {title}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="form-group">
                        <label>Palavras-chave (separadas por vírgula):</label>
                        <input
                            type="text"
                            name="keywords"
                            value={formData.keywords}
                            onChange={handleInputChange}
                            placeholder="SEO, marketing digital, blog, conteúdo"
                        />
                        <small>Adicione palavras-chave relevantes para melhorar o SEO</small>
                    </div>

                    {/* Seleção de Prompt */}
                    <div className="form-group">
                        <label>Prompt (Template) da IA:</label>
                        <div className="prompt-select-row" style={{ display: 'flex', gap: '8px' }}>
                            <select value={selectedPromptId || ''} onChange={(e) => {
                                const val = e.target.value;
                                if (val === '__new__') {
                                    openPromptModal();
                                    return;
                                }
                                const id = val ? parseInt(val) : null;
                                setSelectedPromptId(id);
                                if (id) {
                                    const selected = Array.isArray(prompts) ? prompts.find(p => p.id === id) : null;
                                    if (selected) {
                                        setNewPromptTitle(selected.name || '');
                                        setNewPromptContent(selected.prompt_template || '');
                                        setShowCreatePromptModal(true);
                                    }
                                } else {
                                    setNewPromptTitle('');
                                    setNewPromptContent('');
                                    setShowCreatePromptModal(false);
                                }
                            }}>
                                <option value="">Padrão</option>
                                {Array.isArray(prompts) ? prompts.map(p => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                )) : null}
                                <option value="__new__">+ Criar novo Prompt...</option>
                            </select>
                            <button type="button" className="suggest-btn" onClick={openPromptModal}>
                                Criar Prompt
                            </button>
                        </div>
                        <small>Escolha um template personalizado para orientar a IA.</small>

                        {showCreatePromptModal && (
                            <div className="prompt-modal" style={{ marginTop: '12px', border: '1px solid #ddd', borderRadius: '8px', padding: '12px' }}>
                                <label>Título do Prompt:</label>
                                <input
                                    type="text"
                                    placeholder="Ex: Artigo técnico detalhado"
                                    value={newPromptTitle}
                                    onChange={(e) => setNewPromptTitle(e.target.value)}
                                />
                                <label style={{ marginTop: '8px' }}>Conteúdo do Prompt:</label>
                                <textarea
                                    rows={6}
                                    placeholder="Escreva aqui o texto do prompt/template que a IA deve seguir..."
                                    value={newPromptContent}
                                    onChange={(e) => setNewPromptContent(e.target.value)}
                                />
                                <div className="modal-actions" style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                                    <button type="button" className="suggest-btn" onClick={saveNewPrompt} disabled={creatingPrompt}>
                                        {creatingPrompt ? 'Salvando...' : 'Salvar Prompt'}
                                    </button>
                                    <button type="button" className="clear-btn" onClick={closePromptModal}>
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* System Instruction (Comportamento) */}
                    <div className="form-group system-instruction">
                        <label>Comportamento do System (IA):</label>
                        <textarea
                            rows={4}
                            value={systemInstruction}
                            onChange={(e) => setSystemInstruction(e.target.value)}
                            placeholder={defaultSystemInstruction}
                        />
                        <small>Edite o comportamento do modelo enviado como role "system" na requisição.</small>
                        <div className="char-count">{systemInstruction.length} caracteres</div>
                    </div>

                    {/* Tamanho do Artigo */}
                    <div className="form-row">
                        <div className="form-group">
                            <label>Tamanho do Artigo:</label>
                            <select name="length" value={formData.length} onChange={handleInputChange}>
                                <option value="short">📝 Curto (300-500 palavras)</option>
                                <option value="medium">📄 Médio (800-1200 palavras)</option>
                                <option value="long">📖 Longo (1500-2500 palavras)</option>
                            </select>
                        </div>
                    </div>

                    {/* Referências */}
                    <div className="form-group">
                        <label>Referências (URLs e arquivos .txt):</label>
                        {!Array.isArray(references) || references.length === 0 ? (
                            <p>Nenhuma referência cadastrada ainda.</p>
                        ) : (
                            <div className="references-grid">
                                {references.map(ref => (
                                    <label key={ref.id} className="reference-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={selectedReferences.includes(ref.id)}
                                            onChange={() => toggleReference(ref.id)}
                                        />
                                        <span className="reference-label">
                                            [{ref.type?.toUpperCase()}] {ref.title || (ref.type === 'url' ? ref.value : 'Arquivo')}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        )}
                        <small>Selecione as fontes que a IA deve considerar ao escrever.</small>
                    </div>

                    {/* Adicionar Referência URL */}
                    <div className="form-row">
                        <div className="form-group">
                            <label>Adicionar Referência (URL):</label>
                            <input type="text" placeholder="Título (opcional)" value={newRefTitle} onChange={(e) => setNewRefTitle(e.target.value)} />
                            <input type="text" placeholder="https://exemplo.com/artigo" value={newRefUrl} onChange={(e) => setNewRefUrl(e.target.value)} />
                            <button className="suggest-btn" onClick={addUrlReference}>Adicionar URL</button>
                        </div>

                        {/* Upload de arquivo .txt */}
                        <div className="form-group">
                            <label>Upload de Referência (.txt):</label>
                            <input type="text" placeholder="Título (opcional)" value={newFileTitle} onChange={(e) => setNewFileTitle(e.target.value)} />
                            <input type="file" accept=".txt" onChange={(e) => setNewFileFile(e.target.files?.[0] || null)} />
                            <button className="suggest-btn" onClick={uploadFileReference} disabled={!newFileFile}>Enviar Arquivo</button>
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-inline">
                            <input 
                                type="checkbox"
                                name="saveLocal"
                                checked={formData.saveLocal}
                                onChange={handleInputChange}
                            />
                            <span>💾 Salvar localmente após gerar</span>
                        </label>
                        <label className="checkbox-inline">
                            <input 
                                type="checkbox"
                                name="sendWpDraft"
                                checked={formData.sendWpDraft}
                                onChange={handleInputChange}
                            />
                            <span>📄 Enviar como rascunho ao WordPress</span>
                        </label>
                        <label className="checkbox-inline">
                            <input 
                                type="checkbox"
                                name="sendWpPublish"
                                checked={formData.sendWpPublish}
                                onChange={handleInputChange}
                            />
                            <span>✅ Enviar como publicado ao WordPress</span>
                        </label>
                    </div>

                    <div className="form-actions">
                        <button 
                            className="generate-btn"
                            onClick={generateArticle} 
                            disabled={loading || !formData.topic.trim()}
                        >
                            {loading ? (
                                <>
                                    <Loader size={20} className="spinning" />
                                    Gerando Artigo...
                                </>
                            ) : (
                                <>
                                    <Sparkles size={20} />
                                    Gerar Artigo
                                </>
                            )}
                        </button>

                        <button 
                            className="clear-btn"
                            onClick={clearForm}
                            disabled={loading}
                        >
                            Limpar Formulário
                        </button>
                    </div>
                </div>

                {generatedArticle && (
                    <div className="generated-article card">
                        <div className="article-header">
                            <h3>✨ Artigo Gerado com Sucesso!</h3>
                            {generatedArticle.word_count && (
                                <div className="article-meta">
                                    <span>{generatedArticle.word_count} palavras</span>
                                    <span>•</span>
                                    <span>{generatedArticle.reading_time} min de leitura</span>
                                </div>
                            )}
                        </div>

                        <div className="article-preview">
                            <h4 className="article-title">{generatedArticle.title}</h4>
                            
                            <div className="article-excerpt">
                                <strong>Resumo:</strong> {generatedArticle.excerpt}
                            </div>

                            <div className="article-content">
                                <div 
                                    dangerouslySetInnerHTML={{ __html: generatedArticle.content }}
                                />
                            </div>

                            {generatedArticle.tags && generatedArticle.tags.length > 0 && (
                                <div className="article-tags">
                                    <strong>Tags:</strong>
                                    <div className="tags-list">
                                        {generatedArticle.tags.map((tag, index) => (
                                            <span key={index} className="tag">
                                                #{tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {!formData.saveLocal && (
                            <div className="article-actions">
                                <button onClick={() => saveGeneratedArticle()} className="save-btn">
                                    <Save size={18} />
                                    Salvar Artigo
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ArticleGenerator;